import { Injectable } from '@angular/core';

interface CacheEntry<T> {
  data: T;
  /**
   * Yozilgan payt. Ixtiyoriy, chunki bu maydon keyinroq qo'shildi — undan oldin
   * saqlangan yozuvlarda yo'q va ular "juda eski" deb qaraladi (qayta so'raladi).
   */
  savedAt?: number;
  expiresAt: number;
}

/** Keshdan o'qilgan qiymat: ma'lumotning o'zi va u qachon yozilgani. */
export interface CachedValue<T> {
  data: T;
  /** Yozilgan payt (ms). Noma'lum bo'lsa 0 — ya'ni eskirgan. */
  savedAt: number;
}

export type CacheStoreName = 'products' | 'categories' | 'stores' | 'general';

/**
 * Sahifa yangilanishidan omon qoladigan kesh (IndexedDB).
 *
 * <p>
 * To'g'ridan-to'g'ri chaqirilmaydi — ustidan `ApiCacheService` ishlaydi. Bu sinf
 * faqat "kalit → qiymat + yoshi" saqlashni biladi, qachon so'rov yuborish
 * kerakligini bilmaydi.
 * </p>
 */
@Injectable({ providedIn: 'root' })
export class CacheService {
  private readonly DB_NAME = 'bepos-cache';
  private readonly DB_VERSION = 1;
  private readonly STORES: CacheStoreName[] = ['products', 'categories', 'stores', 'general'];
  private readonly DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 daqiqa

  /**
   * IndexedDB ishlamaydigan muhitlar uchun zaxira: Safari'ning maxfiy oynasi,
   * o'chirilgan saqlash ruxsati, testlar (jsdom).
   *
   * Sahifa yangilanishida yo'qoladi, lekin sessiya ichida takroriy so'rovlarni
   * baribir to'xtatadi — keshsiz qolishdan ko'ra shu yaxshiroq. Muhimi: keshning
   * mavjud emasligi hech qachon xatoga aylanmaydi.
   */
  private readonly fallback = new Map<string, CacheEntry<unknown>>();

  private dbPromise: Promise<IDBDatabase> | null = null;

  /** DB yo'q yoki ochilmasa `null` — chaqiruvchi zaxiraga o'tadi. */
  private async db(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') return null;
    try {
      // Ochish BIR marta boshlanadi; xato bo'lsa keyingi safar qayta urinilmaydi —
      // ruxsat berilmagan brauzerda har chaqiruvda yangi so'rov ochib o'tirmaymiz.
      this.dbPromise ??= this.openDB();
      return await this.dbPromise;
    } catch {
      return null;
    }
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(this.DB_NAME, this.DB_VERSION);
      req.onupgradeneeded = (e) => {
        const db = (e.target as IDBOpenDBRequest).result;
        for (const name of this.STORES) {
          if (!db.objectStoreNames.contains(name)) {
            db.createObjectStore(name);
          }
        }
      };
      req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result);
      req.onerror = (e) => reject((e.target as IDBOpenDBRequest).error);
    });
  }

  private fallbackKey(store: CacheStoreName, key: string): string {
    return `${store}/${key}`;
  }

  async get<T>(store: CacheStoreName, key: string): Promise<CachedValue<T> | null> {
    const entry = await this.readEntry<T>(store, key);
    if (!entry) return null;
    if (Date.now() >= entry.expiresAt) {
      void this.delete(store, key);
      return null;
    }
    return { data: entry.data, savedAt: entry.savedAt ?? 0 };
  }

  private async readEntry<T>(store: CacheStoreName, key: string): Promise<CacheEntry<T> | null> {
    const db = await this.db();
    if (!db) return (this.fallback.get(this.fallbackKey(store, key)) as CacheEntry<T>) ?? null;
    try {
      return await new Promise((resolve) => {
        const tx = db.transaction(store, 'readonly');
        const req = tx.objectStore(store).get(key);
        req.onsuccess = () => resolve((req.result as CacheEntry<T> | undefined) ?? null);
        req.onerror = () => resolve(null);
      });
    } catch {
      return null;
    }
  }

  async set<T>(
    store: CacheStoreName,
    key: string,
    data: T,
    ttlMs = this.DEFAULT_TTL_MS,
  ): Promise<void> {
    const entry: CacheEntry<T> = { data, savedAt: Date.now(), expiresAt: Date.now() + ttlMs };
    const db = await this.db();
    if (!db) {
      this.fallback.set(this.fallbackKey(store, key), entry);
      return;
    }
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        const req = tx.objectStore(store).put(entry, key);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch {
      // Kesh yozilmasligi foydalanuvchiga ko'rsatilmaydi — ilova ishlashda davom etadi.
    }
  }

  async delete(store: CacheStoreName, key: string): Promise<void> {
    this.fallback.delete(this.fallbackKey(store, key));
    const db = await this.db();
    if (!db) return;
    try {
      await new Promise<void>((resolve) => {
        const tx = db.transaction(store, 'readwrite');
        tx.objectStore(store).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      /* empty */
    }
  }

  async clearStore(store: CacheStoreName): Promise<void> {
    for (const k of [...this.fallback.keys()]) {
      if (k.startsWith(`${store}/`)) this.fallback.delete(k);
    }
    const db = await this.db();
    if (!db) return;
    try {
      await new Promise<void>((resolve) => {
        const tx = db.transaction(store, 'readwrite');
        tx.objectStore(store).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      });
    } catch {
      /* empty */
    }
  }

  /**
   * Hamma bo'limni tozalaydi — tizimdan chiqishda ishlatiladi.
   *
   * Ataylab "shu sessiyada yozilganlari" emas, HAMMASI: oldingi sessiyadan qolgan
   * yozuvlar ham boshqa hisobga ko'rinmasligi kerak.
   */
  async clearAll(): Promise<void> {
    this.fallback.clear();
    await Promise.all(this.STORES.map((s) => this.clearStore(s)));
  }
}
